import { Box, Flex, FormErrorMessage, Stack, Text, Textarea } from '@chakra-ui/core'
import { InputWithIcon } from 'components/AdminConsole/commonUI/InputWithIcon'
import { SearchableMenu } from 'components/AdminConsole/commonUI/SearchableMenu'
import { StyledFormControl, StyledFormLabel } from 'components/AdminConsole/commonUI/styles'
import CustomDatePicker from 'components/CommonComponents/CustomDatePicker'
import useLocaleText from 'components/useLocaleText'
import {
  PictureInPicture2,
  SquareChartGantt,
  Workflow,
  ShipWheel,
  RefreshCcw,
  Plus,
  Edit,
  PackageOpen,
} from 'lucide-react'
import React, { useState } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { Job } from 'redux/actions/admin'
import { JobsDailySelector } from './JobsDailySelector'
import { OpenModalTrigger } from 'components/AdminConsole/commonUI/OpenModalTrigger'
import { usePopsModals } from 'components/Pop/Modal/PopsModalsManager'
import { useCompaignModals } from 'components/AdminConsole/Compaign/CompaignModalsManagerContext'
import { RootState } from 'constants/interfaces'
import { useDispatch, useSelector } from 'react-redux'
import { JobFrequencyType, JOBS_ACTION_FREQUENCIES, SERVICE_QUEUE_MAPPING } from '../utils'
import { AppDispatch } from 'config/redux'
import { isNumber } from 'lodash'
import { ICreateJobForm } from '../JobModal'
import dayjs from 'dayjs'
import { ServiceHandlersManager, ServiceActionParams } from './ServiceHandlers'
import keys from 'constants/keys'

const JOBS_ACTION_SERVICES = [
  { id: keys.JOBS_SERVICES.POPS, name: 'jobs_pops', icon: <PictureInPicture2 />, isShowId: false },
  { id: keys.JOBS_SERVICES.COMPAIGN, name: 'jobs_compaign', icon: <PackageOpen />, isShowId: false },
  // { id: 'tasks', name: 'jobs_tasks', icon: <ListChecks />, isShowId: false },
  // { id: 'posts', name: 'jobs_posts', icon: <MessageSquare />, isShowId: false },
  // { id: 'data', name: 'jobs_data', icon: <Database />, isShowId: false },
]

export const JobForm: React.FC<{ job?: Job | null }> = ({ job }) => {
  const dispatch: AppDispatch = useDispatch()
  const { setValue, register, errors, control, getValues, clearError } = useFormContext<ICreateJobForm>()
  const { openPopModal } = usePopsModals()
  const { openCompaignModal } = useCompaignModals()
  const theme = useSelector((state: RootState) => state.general.theme)
  const [isLoading, setIsLoading] = useState(false)

  const selectedActionService = getValues('job_data.action_service') as string
  const actionParams = getValues('job_data.action_params') as ServiceActionParams
  const actionService = getValues('job_data.action_service') as string

  const job_title = useLocaleText('job_title', true)
  const job_title_placeholder = useLocaleText('job_title_placeholder', true)
  const job_desc = useLocaleText('job_description', true)
  const job_desc_placeholder = useLocaleText('job_description_placeholder', true)
  const t_service = useLocaleText('jobs_service', true)
  const t_frequency = useLocaleText('jobs_frequency', true)
  const start_date = useLocaleText('start_date', true)
  const end_date = useLocaleText('end_date', true)
  const admin_select_placeholder = useLocaleText('admin_select_placeholder')
  const create_service = useLocaleText('create_service', true)
  const edit_service = useLocaleText('edit_service', true)
  const error_job_title = useLocaleText('error_job_title', true)
  const error_service_type = useLocaleText('error_service_type', true)
  const error_emty_service = useLocaleText('error_emty_service', true)
  const error_frequency = useLocaleText('error_frequency', true)
  const error_end_date_before_start = useLocaleText('error_end_date_before_start', true)

  const frequency = Number(getValues('job_data.recurrence_frequency'))

  const isServiceDisabled = () => {
    if (!selectedActionService || isLoading) return true
    return ServiceHandlersManager.isServiceDisabledForEdit(selectedActionService, actionParams, job)
  }

  const handleCreateActionParams = async () => {
    if (!selectedActionService) return

    const config = {
      onCreated: (data: ServiceActionParams) => {
        setValue('job_data.action_params', data)
        clearError('job_data.action_params')
        setValue('job_data.data', data)
      },
      clearError,
      dispatch,
      setIsLoading,
      openPopModal,
      openCompaignModal,
    }

    await ServiceHandlersManager.handleServiceAction(selectedActionService, actionParams, config)
  }

  return (
    <Stack spacing={8}>
      <StyledFormControl isInvalid={!!errors.job_data?.title}>
        <StyledFormLabel required>
          <Workflow size={18} strokeWidth={2.5} />
          <Text>{job_title}</Text>
        </StyledFormLabel>
        <InputWithIcon
          w="100%"
          name="job_data.title"
          ref={register({
            required: error_job_title,
          })}
          placeholder={job_title_placeholder}
          autoComplete="job-name"
        />
        <FormErrorMessage>{errors.job_data?.title && errors.job_data?.title.message}</FormErrorMessage>
      </StyledFormControl>

      <StyledFormControl>
        <StyledFormLabel>
          <SquareChartGantt size={18} strokeWidth={2.5} />
          <Text>{job_desc}</Text>
        </StyledFormLabel>

        <Textarea
          name="job_data.content.text"
          ref={register}
          borderRadius="5px"
          resize="none"
          border="1px solid"
          placeholder={job_desc_placeholder}
          _focus={{ boxShadow: '0px 0px 0px 1px black' }}
        />
      </StyledFormControl>

      <StyledFormControl isInvalid={!!errors.job_data?.action_service || !!errors.job_data?.action_params} w="100%">
        <StyledFormLabel required>
          <ShipWheel size={18} strokeWidth={2.5} />
          <Text>{t_service}</Text>
        </StyledFormLabel>
        <Flex w="100%" alignItems="center" style={{ columnGap: '10px' }}>
          <Controller
            name="job_data.action_service"
            control={control}
            rules={{ required: error_service_type }}
            as={
              <SearchableMenu
                isInvalid={!!errors.job_data?.action_service}
                isShowSearch={false}
                items={JOBS_ACTION_SERVICES}
                selectedId={actionService}
                isLocalTranslation
                placeholder={admin_select_placeholder}
                isDisabled={!!job}
                onSelect={(item) => {
                  setValue('job_data.action_service', item.id)
                  setValue('job_data.action_queue_name', SERVICE_QUEUE_MAPPING[item.id])
                  clearError('job_data.action_service')
                }}
              />
            }
          />

          <OpenModalTrigger
            isDisabled={isServiceDisabled()}
            isLoading={isLoading}
            createButtonText={actionParams ? edit_service : create_service}
            rounded="10px !important"
            w="max-content"
            minW="150px"
            display={{ base: 'flex', md: 'flex' }}
            textProps={{ display: { base: 'block', md: 'block' } }}
            color={theme?.elementsColor}
            bg="white"
            border={errors.job_data?.action_params ? '1px solid #e53e3e' : '1px solid #e5e5e5'}
            boxShadow={errors.job_data?.action_params ? '0px 0px 0px 1px #e53e3e' : 'none'}
            icon={actionParams ? <Edit size={16} /> : <Plus size={16} />}
            onCreate={handleCreateActionParams}
          />
        </Flex>

        <FormErrorMessage>
          {(errors.job_data?.action_service && errors.job_data?.action_service.message) ||
            (errors.job_data?.action_params && (errors.job_data?.action_params as any).message)}
        </FormErrorMessage>
      </StyledFormControl>

      <StyledFormControl isInvalid={!!errors.job_data?.recurrence_frequency} w={{ base: '100%', md: '50%' }}>
        <StyledFormLabel required>
          <RefreshCcw size={18} strokeWidth={2.5} />
          <Text>{t_frequency}</Text>
        </StyledFormLabel>

        <Controller
          name="job_data.recurrence_frequency"
          control={control}
          rules={{ required: error_frequency }}
          as={
            <SearchableMenu
              isInvalid={!!errors.job_data?.recurrence_frequency}
              isShowSearch={false}
              items={JOBS_ACTION_FREQUENCIES}
              selectedId={isNumber(frequency) ? frequency?.toString() : JobFrequencyType.ONCE.toString()}
              placeholder={admin_select_placeholder}
              isLocalTranslation={true}
              onSelect={(item) => {
                setValue('job_data.recurrence_frequency', Number(item.id))
                if (item.id !== JobFrequencyType.ONCE.toString()) {
                  setValue('is_recurring', true)
                } else {
                  setValue('is_recurring', false)
                }
                clearError('job_data.recurrence_frequency')
                if (errors.job_data?.recurrence_days_of_week && item.id !== JobFrequencyType.WEEKLY.toString()) {
                  clearError('job_data.recurrence_days_of_week')
                }
              }}
            />
          }
        />

        <FormErrorMessage>
          {errors.job_data?.recurrence_frequency && errors.job_data?.recurrence_frequency.message}
        </FormErrorMessage>
      </StyledFormControl>

      <Stack mb="10px" flexDir={{ base: 'column', md: 'row' }} style={{ columnGap: '10px' }}>
        <StyledFormControl isInvalid={!!errors.job_data?.schedule_start_ts} w={{ base: '100%', md: '50%' }} mb={0}>
          <StyledFormLabel required>
            <Text>{start_date}</Text>
          </StyledFormLabel>
          <Controller
            name="job_data.schedule_start_ts"
            control={control}
            rules={{ required: 'Date is required' }}
            as={CustomDatePicker}
            onChange={([selectedDate]) => selectedDate || null}
            showTimeInput
            timeInputLabel="Time:"
            timeFormat="HH:mm"
            dateFormat="MM/dd/yyyy HH:mm"
            shouldCloseOnSelect={false}
          />
          <FormErrorMessage>
            {errors.job_data?.schedule_start_ts && errors.job_data?.schedule_start_ts.message}
          </FormErrorMessage>
        </StyledFormControl>
        {frequency !== JobFrequencyType.ONCE && (
          <StyledFormControl isInvalid={!!errors.job_data?.schedule_end_ts} w={{ base: '100%', md: '50%' }} mb={0}>
            <StyledFormLabel required>
              <Text>{end_date}</Text>
            </StyledFormLabel>
            <Controller
              name="job_data.schedule_end_ts"
              control={control}
              rules={{
                required: 'Date is required',
                validate: (value) => {
                  const start = getValues('job_data.schedule_start_ts')
                  if (!start || !value) return true

                  const parse = (val: any) => {
                    if (typeof val === 'string' && val.length === 14) {
                      return dayjs(val, 'YYYYMMDDHHmmss')
                    }
                    return dayjs(val)
                  }

                  const startDate = parse(start)
                  const endDate = parse(value)

                  if (!startDate.isValid() || !endDate.isValid()) return true

                  if (endDate.isBefore(startDate)) {
                    return error_end_date_before_start
                  }

                  return true
                },
              }}
              as={CustomDatePicker}
              onChange={([selectedDate]) => selectedDate || null}
              showTimeInput
              timeInputLabel="Time:"
              timeIntervals={5}
              dateFormat="MM/dd/yyyy HH:mm"
              shouldCloseOnSelect={false}
            />
            <FormErrorMessage>
              {errors.job_data?.schedule_end_ts && errors.job_data?.schedule_end_ts.message}
            </FormErrorMessage>
          </StyledFormControl>
        )}
      </Stack>
      {frequency === JobFrequencyType.WEEKLY && (
        <StyledFormControl isInvalid={!!errors.job_data?.recurrence_days_of_week}>
          <Box
            border={errors.job_data?.recurrence_days_of_week ? '1px solid #e53e3e' : '1px solid #e5e5e5'}
            boxShadow={errors.job_data?.recurrence_days_of_week ? '0px 0px 0px 1px #e53e3e' : 'none'}
            p={2}
            bg="white"
            borderRadius="10px"
            style={{ filter: frequency === JobFrequencyType.WEEKLY ? 'blur(0px)' : 'blur(1px)' }}
            pointerEvents={frequency === JobFrequencyType.WEEKLY ? 'auto' : 'none'}
          >
            <JobsDailySelector isActive={frequency === JobFrequencyType.WEEKLY} />
          </Box>
          <FormErrorMessage>
            {/* @ts-ignore */}
            {errors.job_data?.recurrence_days_of_week && errors.job_data?.recurrence_days_of_week.message}
          </FormErrorMessage>
        </StyledFormControl>
      )}

      <Controller
        name="job_data.action_params"
        control={control}
        rules={{ required: error_emty_service }}
        defaultValue={null}
        as={<Box display="none" />}
      />

      {/* Default values for the job data */}
      <Controller name="job_data.initiator" control={control} defaultValue={1} as={<Box display="none" />} />
      <Controller name="is_recurring" control={control} defaultValue={false} as={<Box display="none" />} />
      <Controller name="job_data.recurrence_interval" control={control} defaultValue={1} as={<Box display="none" />} />
      <Controller
        name="job_data.action_queue_name"
        control={control}
        defaultValue={SERVICE_QUEUE_MAPPING[actionService || keys.JOBS_SERVICES.POPS]}
        as={<Box display="none" />}
      />
      <Controller name="job_data.action_object_id" control={control} defaultValue={null} as={<Box display="none" />} />
    </Stack>
  )
}
